import { VISUALISATION_KEYS } from 'adapter/in/advanced-analytics/analysis/GlobalSettings/GeneralTab/GlobalSettingsVisualisation';

import React, { createContext, ReactElement, useContext, useReducer } from 'react';

export enum ACTION_TYPE {
    SET_ANALYSIS_UI_RESTRICTIONS = 'SET_ANALYSIS_UI_RESTRICTIONS',
    SET_PRESETPAGE_UI_RESTRICTIONS = 'SET_PRESETPAGE_UI_RESTRICTIONS',
    SET_NEW_ANALYSIS_UI_RESTRICTIONS = 'SET_NEW_ANALYSIS_UI_RESTRICTIONS',
    SET_DASHBOARD_ANALYSIS_UI_RESTRICTIONS = 'SET_DASHBOARD_ANALYSIS_UI_RESTRICTIONS',
    SET_AI_DESCRIPTION_ALLOWED = 'SET_AI_DESCRIPTION_ALLOWED',
    CLEAR_UI_RESTRICTIONS = 'CLEAR_UI_RESTRICTIONS',
}

export type UIRestrictionsState = {
    availableVisualisations: VISUALISATION_KEYS[];
    showPresetsAsCards: boolean;
    enableAddDatablock: boolean;
    predefinedPresets: string[];
    editTitleTab: boolean;
    editTitleIcon: boolean;
    enableSave: boolean;
    enableDropdownActions: boolean;
    aiDescriptionAllowed: boolean;
};

export const initialState: UIRestrictionsState = {
    availableVisualisations: [],
    showPresetsAsCards: true,
    enableAddDatablock: false,
    predefinedPresets: [],
    editTitleTab: true,
    editTitleIcon: true,
    enableSave: true,
    enableDropdownActions: true,
    aiDescriptionAllowed: false,
};

export type SetAnalysisUIRestrictions = {
    type: ACTION_TYPE.SET_ANALYSIS_UI_RESTRICTIONS;
    payload: VISUALISATION_KEYS[];
};
export type SetPresetPageUIRestrictions = {
    type: ACTION_TYPE.SET_PRESETPAGE_UI_RESTRICTIONS;
    payload: { visualisations: VISUALISATION_KEYS[]; presets: string[] };
};
export type SetNewAnalysisUIRestrictions = {
    type: ACTION_TYPE.SET_NEW_ANALYSIS_UI_RESTRICTIONS;
    payload?: VISUALISATION_KEYS[];
};

export type SetDashboardAnalysisUIRestrictions = {
    type: ACTION_TYPE.SET_DASHBOARD_ANALYSIS_UI_RESTRICTIONS;
    payload: VISUALISATION_KEYS[];
};

export type SetAiDescriptionAllowed = {
    type: ACTION_TYPE.SET_AI_DESCRIPTION_ALLOWED;
    payload: boolean;
};

type Actions =
    | SetAnalysisUIRestrictions
    | SetPresetPageUIRestrictions
    | SetNewAnalysisUIRestrictions
    | SetDashboardAnalysisUIRestrictions
    | SetAiDescriptionAllowed;

export const useUIRestrictionsStateReducer = (state: UIRestrictionsState, action: Actions): UIRestrictionsState => {
    const { type } = action;
    switch (type) {
        case ACTION_TYPE.SET_AI_DESCRIPTION_ALLOWED: {
            return { ...state, aiDescriptionAllowed: action.payload };
        }
        case ACTION_TYPE.SET_PRESETPAGE_UI_RESTRICTIONS: {
            return {
                availableVisualisations: action.payload.visualisations,
                showPresetsAsCards: false,
                enableAddDatablock: false,
                predefinedPresets: action.payload.presets,
                editTitleTab: false,
                editTitleIcon: true,
                enableSave: false,
                enableDropdownActions: false,
                aiDescriptionAllowed: state.aiDescriptionAllowed,
            };
        }
        case ACTION_TYPE.SET_NEW_ANALYSIS_UI_RESTRICTIONS: {
            return {
                availableVisualisations: action.payload ? action.payload : state.availableVisualisations,
                showPresetsAsCards: true,
                enableAddDatablock: true,
                predefinedPresets: [],
                editTitleTab: true,
                editTitleIcon: true,
                enableSave: true,
                enableDropdownActions: false,
                aiDescriptionAllowed: state.aiDescriptionAllowed,
            };
        }
        case ACTION_TYPE.SET_DASHBOARD_ANALYSIS_UI_RESTRICTIONS: {
            return {
                availableVisualisations: action.payload,
                showPresetsAsCards: true,
                enableAddDatablock: true,
                predefinedPresets: [],
                editTitleTab: true,
                editTitleIcon: false,
                enableSave: true,
                enableDropdownActions: false,
                aiDescriptionAllowed: state.aiDescriptionAllowed,
            };
        }
        case ACTION_TYPE.SET_ANALYSIS_UI_RESTRICTIONS:
        default: {
            return {
                availableVisualisations: action.payload,
                showPresetsAsCards: true,
                enableAddDatablock: true,
                predefinedPresets: [],
                editTitleTab: true,
                editTitleIcon: true,
                enableSave: true,
                enableDropdownActions: true,
                aiDescriptionAllowed: state.aiDescriptionAllowed,
            };
        }
    }
};

type UIRestrictionsStateDispatch = {
    setAnalysisUIRestrictions: (visualisations: VISUALISATION_KEYS[]) => void;
    setNewAnalysisUIRestrictions: (visualisations?: VISUALISATION_KEYS[]) => void;
    setPresetPageUIRestrictions: (visualisations: VISUALISATION_KEYS[], presets: string[]) => void;
    setDashboardAnalysisUIRestrictions: (visualisations: VISUALISATION_KEYS[]) => void;
    setAiDescriptionAllowed: (allowed: boolean) => void;
};

export type UIRestrictionsProviderProps = {
    children: ReactElement;
};

export const UIRestrictionsDispatchContext = createContext({} as UIRestrictionsStateDispatch);
const UIRestrictionsStateContext = createContext({} as UIRestrictionsState);

const dispatchUIRestrictions = (): UIRestrictionsStateDispatch => useContext(UIRestrictionsDispatchContext);
const useUIRestrictions = (): UIRestrictionsState => useContext(UIRestrictionsStateContext);

const UIRestrictionsProvider = (props: UIRestrictionsProviderProps): ReactElement => {
    const { children } = props;
    const [state, dispatch] = useReducer(useUIRestrictionsStateReducer, initialState);

    const setAnalysisUIRestrictions = (visualisations: VISUALISATION_KEYS[]) => {
        dispatch({ type: ACTION_TYPE.SET_ANALYSIS_UI_RESTRICTIONS, payload: visualisations });
    };

    const setNewAnalysisUIRestrictions = (visualisations?: VISUALISATION_KEYS[]) => {
        dispatch({ type: ACTION_TYPE.SET_NEW_ANALYSIS_UI_RESTRICTIONS, payload: visualisations });
    };

    const setPresetPageUIRestrictions = (visualisations: VISUALISATION_KEYS[], presets: string[]) => {
        dispatch({ type: ACTION_TYPE.SET_PRESETPAGE_UI_RESTRICTIONS, payload: { visualisations, presets } });
    };

    const setDashboardAnalysisUIRestrictions = (visualisations: VISUALISATION_KEYS[]) => {
        dispatch({ type: ACTION_TYPE.SET_DASHBOARD_ANALYSIS_UI_RESTRICTIONS, payload: visualisations });
    };

    const setAiDescriptionAllowed = (allowed: boolean) => {
        dispatch({ type: ACTION_TYPE.SET_AI_DESCRIPTION_ALLOWED, payload: allowed });
    };

    return (
        <UIRestrictionsStateContext.Provider value={state}>
            <UIRestrictionsDispatchContext.Provider
                value={{
                    setAnalysisUIRestrictions,
                    setNewAnalysisUIRestrictions,
                    setPresetPageUIRestrictions,
                    setDashboardAnalysisUIRestrictions,
                    setAiDescriptionAllowed,
                }}
            >
                {children}
            </UIRestrictionsDispatchContext.Provider>
        </UIRestrictionsStateContext.Provider>
    );
};

export { UIRestrictionsProvider, dispatchUIRestrictions, useUIRestrictions };
